import { Link } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { categories } from "@/mocks/categories";
import { reviews, site } from "@/mocks/site";
import { useSeo } from "@/hooks/useSeo";

const principles = [
  { icon: "ri-scales-3-line", title: "立场克制", desc: "我们不做情绪放大器，尽量呈现事实与上下文，把判断权交还给你。" },
  { icon: "ri-file-search-line", title: "来源可溯", desc: "重要信息标注来源线索与时间节点，方便你进一步查证与核验。" },
  { icon: "ri-heart-3-line", title: "尊重每一位当事人", desc: "涉及人物与机构时避免无据揣测，尊重隐私与名誉，谨慎表达。" },
  { icon: "ri-refund-2-line", title: "欢迎纠错", desc: "我们不是权威，也会出错。发现问题欢迎反馈，核实后一定及时更正。" },
];

const timeline = [
  { year: "2020", title: "从整理开始", desc: "最初只是编辑部几位伙伴手动整理感兴趣的主题清单，分享给身边的朋友。" },
  { year: "2022", title: "确立四大栏目", desc: "把内容正式归类为话题、事件、人物、机构四大方向，形成可循的内容体系。" },
  { year: "2024", title: "上线分类导航", desc: "推出导航爆料的分类浏览形态，让「想追哪个瓜就进哪一栏」成为现实。" },
  { year: "至今", title: "持续做减法", desc: "坚持少而精的整理，只把值得被看见的内容轻轻放好，不做噪音的搬运工。" },
];

const jsonLd = {
  "@context": "https://schema.org",
  "@type": "AboutPage",
  name: `关于我们 - ${site.name}`,
  description: `${site.name}的介绍、定位、发展理念与内容原则。`,
  inLanguage: "zh-CN",
  about: { "@type": "Organization", name: site.name },
};

export default function AboutPage() {
  useSeo({
    title: "关于我们 | 导航爆料",
    description:
      "了解导航爆料的品牌定位、发展理念与内容原则。我们按话题、事件、人物、机构分类整理信息，让浏览回归清楚与高效。",
    keywords: "导航爆料,关于我们,内容原则,分类整理",
    path: "/about",
    jsonLd: JSON.stringify(jsonLd),
  });

  return (
    <>
      <Breadcrumb crumbs={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />
      <section className="mx-auto w-full max-w-7xl px-4 md:px-6">
        {/* 头部 */}
        <div className="relative mt-6 overflow-hidden rounded-xl bg-gradient-to-br from-primary-100 via-background-100 to-accent-100 px-6 py-12 md:px-12 md:py-16">
          <div className="max-w-2xl">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-background-50/80 px-4 py-1.5 text-xs font-semibold text-primary-700">
              <i className="ri-information-line"></i>About us
            </span>
            <h1 className="mt-4 font-heading text-3xl leading-tight font-black text-foreground-950 md:text-4xl">
              我们是耐心的整理者
            </h1>
            <p className="mt-4 text-base leading-7 text-foreground-700">
              信息早已不是稀缺品，「值得看什么」才是。{site.name}
              想当一个安静的图书管理员，把热点按话题、事件、人物与机构放上书架，让你想找什么，就能轻松找到什么。
            </p>
          </div>
        </div>

        {/* 定位数据 */}
        <div className="mt-8 grid grid-cols-2 gap-4 lg:grid-cols-4">
          {[
            { v: "2,486", l: "条整理内容" },
            { v: "4", l: "大核心栏目" },
            { v: "5+", l: "年内容整理经验" },
            { v: "24h", l: "热点跟进节奏" },
          ].map((s) => (
            <div key={s.l} className="rounded-lg border border-background-200/70 bg-background-100 p-5 text-center">
              <p className="font-heading text-2xl font-black text-primary-600 md:text-3xl">{s.v}</p>
              <p className="mt-1 text-sm text-foreground-600">{s.l}</p>
            </div>
          ))}
        </div>

        {/* 四大栏目 */}
        <div className="mt-12">
          <h2 className="font-heading text-2xl font-bold text-foreground-950">我们如何给内容分类</h2>
          <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {categories.map((c) => (
              <Link
                key={c.slug}
                to={`/category/${c.slug}`}
                className="group rounded-lg border border-background-200/70 bg-background-100 p-5 transition-all hover:border-primary-300 hover:bg-primary-50"
              >
                <span className="flex h-10 w-10 items-center justify-center rounded-md bg-primary-100 text-lg text-primary-700">
                  <i className={c.icon}></i>
                </span>
                <h3 className="mt-3 font-heading text-base font-bold text-foreground-950 group-hover:text-primary-700">{c.name}</h3>
                <p className="mt-1.5 text-sm leading-6 text-foreground-600 line-clamp-2">{c.tagline}。{c.blurb}</p>
              </Link>
            ))}
          </div>
        </div>

        {/* 发展历程 */}
        <div className="mt-12 overflow-hidden rounded-lg border border-background-200/70 bg-background-100">
          <div className="border-b border-background-200 px-6 py-5">
            <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-950">
              <i className="ri-road-map-line text-primary-500"></i>发展历程
            </h2>
          </div>
          <div className="p-6">
            <div className="relative ml-2 flex flex-col gap-6 border-l-2 border-background-200 pl-6">
              {timeline.map((t) => (
                <div key={t.year} className="relative">
                  <span className="absolute top-1 -left-[33px] flex h-3.5 w-3.5 items-center justify-center rounded-full bg-primary-500 ring-4 ring-primary-100"></span>
                  <span className="font-heading text-lg font-black text-primary-600">{t.year}</span>
                  <h3 className="mt-0.5 font-bold text-foreground-950">{t.title}</h3>
                  <p className="mt-1 text-sm leading-6 text-foreground-600">{t.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* 内容原则 */}
        <div className="mt-12">
          <h2 className="font-heading text-2xl font-bold text-foreground-950">我们坚持的内容原则</h2>
          <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
            {principles.map((p) => (
              <div key={p.title} className="flex gap-4 rounded-lg border border-background-200/70 bg-background-100 p-5">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-accent-100 text-xl text-accent-700">
                  <i className={p.icon}></i>
                </span>
                <div>
                  <h3 className="font-heading text-base font-bold text-foreground-950">{p.title}</h3>
                  <p className="mt-1.5 text-sm leading-6 text-foreground-600">{p.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* 口碑 */}
        <div className="mt-12">
          <h2 className="font-heading text-2xl font-bold text-foreground-950">读者的声音</h2>
          <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-3">
            {reviews.map((r) => (
              <figure key={r.name} className="rounded-lg border border-background-200/70 bg-background-100 p-5">
                <blockquote className="text-sm leading-7 text-foreground-700">“{r.text}”</blockquote>
                <figcaption className="mt-4 flex items-center gap-3">
                  <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary-100 text-sm font-bold text-primary-700">
                    {r.name.slice(0, 1)}
                  </span>
                  <span className="text-sm font-semibold text-foreground-950">
                    {r.name} <span className="ml-1 font-normal text-foreground-500">· {r.role}</span>
                  </span>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>

        {/* 联系 */}
        <div className="my-12 overflow-hidden rounded-xl bg-foreground-950 text-background-50">
          <div className="flex flex-col gap-6 px-6 py-10 md:flex-row md:items-center md:justify-between md:px-12">
            <div>
              <h2 className="font-heading text-2xl font-bold">想聊聊、想纠错、还是想合作？</h2>
              <p className="mt-2 text-sm text-background-200">欢迎通过邮箱联系我们，工作日会在 24 小时内回复。</p>
            </div>
            <a
              href={`mailto:${site.email}`}
              className="inline-flex shrink-0 cursor-pointer items-center gap-2 rounded-full bg-primary-500 px-6 py-3 text-sm font-semibold whitespace-nowrap text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-mail-send-line"></i>
              {site.email}
            </a>
          </div>
        </div>
      </section>
    </>
  );
}